import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useDirectDispatch, ensureDispatch, saveDispatch, type DispatchStop } from "@/hooks/useDirectDispatch";
import { useShipperAddresses, type ShipperAddress } from "@/hooks/useShipperAddresses";
import { readDispatchDraft, writeDispatchDraft, clearDispatchDraft, isDraftNewerThan } from "@/hooks/lrDirect/useDispatchDraft";
import { useChatSystem } from "@/hooks/useChatSystem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { AddressBookAutocomplete } from "@/components/lr-direct/AddressBookAutocomplete";
import { DispatchStatusBadge } from "@/components/lr-direct/DispatchStatusBadge";
import { DispatchDocumentsPanel } from "@/components/lr-direct/DispatchDocumentsPanel";
import { DispatchTimeline } from "@/components/lr-direct/DispatchTimeline";
import { BrokerDispatchView } from "@/components/lr-direct/BrokerDispatchView";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { type DispatchStatus } from "@/hooks/useUpdateDispatchStatus";
import { closeLoadManual } from "@/lib/lrDirect/canonicalMilestones";
import { notifyClient } from "@/lib/notify";
import { brokerDispatchLink } from "@/lib/lrDirect/notifyLinks";
import { buildDispatchSnapshot, diffDispatchSnapshots, summarizeChanges } from "@/lib/lrDirect/dispatchChangeDetect";
import { ArrowLeft, ArrowUp, ArrowDown, Printer, Download, Save, ShieldCheck, MessageSquare, ClipboardList, Plus, Trash2, Lock, BookmarkPlus, BookUser, CheckCircle2, BookmarkIcon, Eye } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useShipperDispatchTemplates, type ShipperDispatchTemplate } from "@/hooks/useShipperDispatchTemplates";
import { SaveAsTemplateModal } from "@/components/lr-direct/dispatch/SaveAsTemplateModal";
import { TemplatePickerDropdown } from "@/components/lr-direct/dispatch/TemplatePickerDropdown";

type BuilderStop = DispatchStop & { _saveToBook?: boolean };

interface BuilderProps { readOnly?: boolean }

export default function LRDirectDispatchBuilderPage({ readOnly = false }: BuilderProps) {
  usePageMeta({ title: "LR Direct — Dispatch Builder | LaneRunner", description: "Build and assign the dispatch packet for an LR Direct load." });
  const { loadId } = useParams<{ loadId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openChatWithUser } = useChatSystem();
  const { addresses } = useShipperAddresses(user?.id);

  const [load, setLoad] = useState<any>(null);
  const [stops, setStops] = useState<BuilderStop[]>([]);
  const [pallets, setPallets] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [commodity, setCommodity] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [brokerProfile, setBrokerProfile] = useState<{ id: string; name: string } | null>(null);
  const [awardedAmount, setAwardedAmount] = useState<number | null>(null);
  const [awardedAt, setAwardedAt] = useState<string | null>(null);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [transitConfirm, setTransitConfirm] = useState<null | { markReady: boolean }>(null);

  /**
   * `dirty` = the user has touched the form since the last seed/save. While dirty
   * we (a) suspend background refetches so nothing can wipe the edits and
   * (b) mirror the form into localStorage as a crash backstop.
   */
  const [dirty, setDirty] = useState(false);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const markDirty = () => setDirty(true);

  // Only the editable owner form needs the refetch pause. Read-only broker /
  // locked shipper views keep realtime + focus self-heal fully intact.
  const { data, initialLoading, refreshing, refresh } = useDirectDispatch(loadId, {
    suspendRefresh: dirty && !readOnly,
  });

  const { templates, loading: tplLoading, save: saveTemplate, remove: removeTemplate, apply: applyTemplateRpc } =
    useShipperDispatchTemplates(user?.id);

  const hasUnsavedContent =
    stops.some((s) => s.name || s.address || s.city || s.contact_name || s.instructions) ||
    !!pallets || !!weight || !!commodity.trim() || !!notes.trim();


  const buildTemplatePayload = () => ({
    version: 1,
    pallets: pallets || null,
    weight: weight || null,
    commodity: commodity || null,
    notes: notes || null,
    stops: stops.map((s) => ({
      stop_type: s.stop_type,
      saved_address_id: s.saved_address_id ?? null,
      name: s.name ?? null,
      address: s.address ?? null,
      city: s.city ?? null,
      state: s.state ?? null,
      postal_code: s.postal_code ?? null,
      country: s.country ?? null,
      latitude: s.latitude ?? null,
      longitude: s.longitude ?? null,
      contact_name: s.contact_name ?? null,
      contact_phone: s.contact_phone ?? null,
      contact_email: s.contact_email ?? null,
      instructions: s.instructions ?? null,
      appointment_window: (s as any).appointment_window ?? null,
    })),
  });

  const applyTemplate = async (tpl: ShipperDispatchTemplate) => {
    try {
      // Fire-and-forget RPC bumps use_count / last_used_at and re-validates ownership.
      await applyTemplateRpc.mutateAsync(tpl.id).catch(() => {});
      markDirty();
      const payload = tpl.template_data || {};
      if (payload.pallets != null) setPallets(String(payload.pallets));
      if (payload.weight != null) setWeight(String(payload.weight));
      if (payload.commodity != null) setCommodity(String(payload.commodity ?? ""));
      if (payload.notes != null) setNotes(String(payload.notes ?? ""));
      const tplStops: any[] = Array.isArray(payload.stops) ? payload.stops : [];
      if (tplStops.length > 0) {
        const newStops: BuilderStop[] = tplStops.map((s, i) => {
          // Re-resolve saved address against the live address book so updates flow through.
          const fresh = s.saved_address_id ? addresses.find((a) => a.id === s.saved_address_id) : null;
          if (fresh) {
            return {
              stop_number: i + 1,
              stop_type: s.stop_type === "delivery" ? "delivery" : "pickup",
              saved_address_id: fresh.id,
              name: fresh.name, address: fresh.address, city: fresh.city, state: fresh.state,
              postal_code: fresh.postal_code, country: fresh.country,
              latitude: fresh.latitude, longitude: fresh.longitude,
              contact_name: fresh.default_contact_name,
              contact_phone: fresh.default_contact_phone,
              contact_email: fresh.default_contact_email,
              instructions: fresh.default_instructions,
              scheduled_date: null,
              _saveToBook: false,
            } as BuilderStop;
          }
          return {
            stop_number: i + 1,
            stop_type: s.stop_type === "delivery" ? "delivery" : "pickup",
            saved_address_id: null,
            name: s.name ?? undefined,
            address: s.address ?? undefined,
            city: s.city ?? undefined,
            state: s.state ?? undefined,
            postal_code: s.postal_code ?? undefined,
            country: s.country ?? undefined,
            latitude: s.latitude ?? undefined,
            longitude: s.longitude ?? undefined,
            contact_name: s.contact_name ?? undefined,
            contact_phone: s.contact_phone ?? undefined,
            contact_email: s.contact_email ?? undefined,
            instructions: s.instructions ?? undefined,
            scheduled_date: null,
            _saveToBook: false,
          } as BuilderStop;
        });
        setStops(newStops);
      }
      toast.success(`Applied template "${tpl.name}"`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to apply template");
    }
  };

  // Fetch the underlying load summary.
  // Per-load state is cleared up-front so a previously opened builder's load can
  // never linger and become the seed source for the load now in the URL.
  useEffect(() => {
    if (!loadId) return;
    let cancelled = false;
    setLoad(null);
    setAwardedAmount(null);
    setAwardedAt(null);
    setBrokerProfile(null);
    seededRef.current = null;
    (async () => {
      const { data: l } = await supabase
        .from("direct_loads")
        .select("id, load_number, origin_name, origin_address, origin_city, origin_state, origin_postal_code, destination_name, destination_address, destination_city, destination_state, destination_postal_code, pickup_date, delivery_date, equipment_type, weight_lbs, length_ft, load_type, commodity, status, awarded_bid_id, awarded_at, total_miles, created_by, special_instructions, shipper_notes_to_brokers, handling_requirements, appointment_type, reference_number")
        .eq("id", loadId).maybeSingle();
      if (cancelled) return; // stale response from a previous loadId
      setLoad(l);
      setAwardedAt((l as any)?.awarded_at ?? null);
      if ((l as any)?.awarded_bid_id) {
        const { data: bid } = await supabase
          .from("direct_bids")
          .select("broker_id, bid_amount, submitted_at")
          .eq("id", (l as any).awarded_bid_id)
          .maybeSingle();
        if (cancelled) return;
        if (bid?.bid_amount != null) setAwardedAmount(Number(bid.bid_amount));
        if (!(l as any)?.awarded_at && (bid as any)?.submitted_at) setAwardedAt((bid as any).submitted_at);
        if (bid?.broker_id) {
          const { data: p } = await supabase
            .from("profiles")
            .select("id, first_name, last_name, email")
            .eq("id", bid.broker_id)
            .maybeSingle();
          if (cancelled) return;
          const prof: any = p;
          if (prof) {
            const name = [prof.first_name, prof.last_name].filter(Boolean).join(" ") || prof.email || "Broker";
            setBrokerProfile({ id: prof.id, name });
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [loadId]);

  /**
   * Seed local state from dispatch + load — ONCE per load, and never while the
   * form is dirty. A background refetch produces a new `data` identity; before
   * this guard existed that re-seed silently overwrote whatever the shipper was
   * typing. `seedTokenRef` is reset after a successful save so fresh server
   * values re-hydrate the form then (and only then).
   */
  const seededRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  useEffect(() => {
    if (!data) return;
    if (dirtyRef.current) return;                     // never clobber live edits
    if (seededRef.current === (loadId ?? "")) return; // already seeded for this load

    // ONLY the load in the URL may be a seed source. A row still in state from a
    // previously opened builder must never pre-fill city/state for this load.
    const srcLoad = load && loadId && load.id === loadId ? load : null;
    const existingStops = (data.stops ?? []).map((s) => ({ ...s, _saveToBook: false }));
    const needsLoadSeed = !data.exists || !data.dispatch || existingStops.length === 0;
    if (needsLoadSeed && !srcLoad) return;             // wait for the right load — seed nothing

    seededRef.current = loadId ?? "";

    if (data.exists && data.dispatch) {
      const d = data.dispatch;
      setPallets(d.load_pallet_count != null ? String(d.load_pallet_count) : "");
      setWeight(d.load_weight_lbs != null ? String(d.load_weight_lbs) : "");
      setCommodity(d.load_commodity ?? "");
      setNotes(d.confidential_notes ?? "");
      if (existingStops.length === 0 && srcLoad) {
        // Existing dispatch draft with no stops — seed the minimum one pickup + one delivery
        setStops([
          {
            stop_number: 1, stop_type: "pickup",
            city: srcLoad.origin_city, state: srcLoad.origin_state,
            scheduled_date: srcLoad.pickup_date, _saveToBook: true,
          },
          {
            stop_number: 2, stop_type: "delivery",
            city: srcLoad.destination_city, state: srcLoad.destination_state,
            scheduled_date: srcLoad.delivery_date ?? null, _saveToBook: true,
          },
        ]);
      } else {
        setStops(existingStops);
      }
    } else if (srcLoad) {
      // Seed initial stops from the load (one pickup at origin, one delivery at destination)
      setStops([
        {
          stop_number: 1, stop_type: "pickup",
          city: srcLoad.origin_city, state: srcLoad.origin_state,
          scheduled_date: srcLoad.pickup_date, _saveToBook: true,
        },
        {
          stop_number: 2, stop_type: "delivery",
          city: srcLoad.destination_city, state: srcLoad.destination_state,
          scheduled_date: srcLoad.delivery_date ?? null, _saveToBook: true,
        },
      ]);
      setWeight(srcLoad.weight_lbs ? String(srcLoad.weight_lbs) : "");
      setCommodity(srcLoad.commodity ?? "");
    }


    // localStorage backstop: restore a draft only when it is NEWER than the
    // dispatch row's last server-side save, so stale drafts can't resurrect.
    if (loadId && !readOnly) {
      const draft = readDispatchDraft(loadId);
      if (draft && isDraftNewerThan(draft, (data.dispatch as any)?.updated_at ?? null)) {
        setPallets(draft.pallets ?? "");
        setWeight(draft.weight ?? "");
        setCommodity(draft.commodity ?? "");
        setNotes(draft.notes ?? "");
        if (Array.isArray(draft.stops) && draft.stops.length > 0) setStops(draft.stops as BuilderStop[]);
        setRestoredDraft(true);
        setDirty(true);
      } else if (draft) {
        clearDispatchDraft(loadId);
      }
    }
  }, [data, load, loadId, readOnly]);

  // Mirror the dirty form into localStorage (debounced) as a crash/reload backstop.
  useEffect(() => {
    if (!loadId || readOnly || !dirty) return;
    const t = setTimeout(() => {
      writeDispatchDraft(loadId, { pallets, weight, commodity, notes, stops });
    }, 400);
    return () => clearTimeout(t);
  }, [loadId, readOnly, dirty, pallets, weight, commodity, notes, stops]);

  // Cheap insurance: warn before leaving with unsaved content.
  useEffect(() => {
    if (readOnly || !dirty || !hasUnsavedContent) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [readOnly, dirty, hasUnsavedContent]);

  const discardDraft = () => {
    if (loadId) clearDispatchDraft(loadId);
    setRestoredDraft(false);
    setDirty(false);
    seededRef.current = null; // force a clean re-seed from the server payload
    refresh();
  };


  const isOwner = data?.is_owner ?? false;
  const isBrokerView = readOnly || (!isOwner && data?.unlocked);
  const dispatchStatus = data?.dispatch?.status ?? "draft";

  // Stage-aware mode flags (Item 3 / Option C)
  const IN_TRANSIT_DS = ["en_route_to_pickup", "at_pickup", "picked_up", "en_route_to_delivery", "at_delivery", "in_transit"];
  const POST_READY_DS = ["ready", "assigned", ...IN_TRANSIT_DS];
  const loadStatus: string = load?.status ?? "";
  const isPreAward = loadStatus === "posted"; // Bidding stage — no broker attached yet
  const isReadOnlyStage =
    loadStatus === "delivered" ||
    loadStatus === "completed" ||
    loadStatus === "canceled" ||
    loadStatus === "expired" ||
    dispatchStatus === "delivered" ||
    dispatchStatus === "closed";
  const isInTransit = IN_TRANSIT_DS.includes(dispatchStatus);
  const lockedView = readOnly || isReadOnlyStage;

  const updateStop = (idx: number, patch: Partial<BuilderStop>) => {
    markDirty();
    setStops((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const applyAddress = (idx: number, addr: ShipperAddress) =>
    updateStop(idx, {
      saved_address_id: addr.id,
      name: addr.name, address: addr.address, city: addr.city, state: addr.state,
      postal_code: addr.postal_code, country: addr.country,
      latitude: addr.latitude, longitude: addr.longitude,
      contact_name: addr.default_contact_name, contact_phone: addr.default_contact_phone,
      contact_email: addr.default_contact_email,
      instructions: addr.default_instructions,
      _saveToBook: false, // already in book
    });

  const addStop = (type: "pickup" | "delivery") => {
    markDirty();
    setStops((prev) => [...prev, { stop_number: prev.length + 1, stop_type: type, _saveToBook: true }]);
  };
  const removeStop = (idx: number) => {
    markDirty();
    setStops((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stop_number: i + 1 })));
  };
  /** Move a stop up/down in the route sequence. Types are preserved — only the
   *  position changes — and stop_number is renumbered from array order (which is
   *  also what saveDispatch persists). Goes through markDirty/setStops so the
   *  dirty guard + localStorage draft pick it up like any other edit. */
  const moveStop = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    setStops((prev) => {
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((s, i) => ({ ...s, stop_number: i + 1 }));
    });
    if (target >= 0) markDirty();
  };


  const doSave = async (markReady: boolean) => {
    if (!loadId || !user?.id) return;
    setSaving(true);
    // Capture pre-save status so we know whether this counts as a "post-ready edit"
    const preSaveStatus = dispatchStatus;
    const wasPostReady = POST_READY_DS.includes(preSaveStatus);
    const wasInTransit = IN_TRANSIT_DS.includes(preSaveStatus);
    // Snapshot the server-side values BEFORE the write so we can tell a real
    // edit from an open-and-save with no changes (noise guard).
    const beforeSnapshot = buildDispatchSnapshot({
      pallets: data?.dispatch?.load_pallet_count ?? null,
      weight: data?.dispatch?.load_weight_lbs ?? null,
      commodity: data?.dispatch?.load_commodity ?? null,
      notes: data?.dispatch?.confidential_notes ?? null,
      stops: (data?.stops ?? []) as any[],
    });
    try {
      const dispatchId = data?.dispatch?.id ?? (await ensureDispatch(loadId, user.id));


      // Persist new addresses into the shipper's address book (per-shipper RLS)
      const stopsToSave: BuilderStop[] = [];
      for (const s of stops) {
        let savedId = s.saved_address_id ?? null;
        if (!savedId && s._saveToBook !== false && (s.name || s.address) && s.city && s.state) {
          const { data: ins } = await supabase
            .from("direct_shipper_addresses" as any)
            .insert({
              shipper_id: user.id,
              label: s.name || `${s.city}, ${s.state}`,
              name: s.name ?? null,
              address: s.address ?? null,
              city: s.city, state: s.state,
              postal_code: s.postal_code ?? null,
              country: s.country ?? "US",
              latitude: s.latitude ?? null,
              longitude: s.longitude ?? null,
              default_contact_name: s.contact_name ?? null,
              default_contact_phone: s.contact_phone ?? null,
              default_contact_email: s.contact_email ?? null,
              default_instructions: s.instructions ?? null,
              use_count: 1,
              last_used_at: new Date().toISOString(),
            })
            .select("id")
            .single();
          savedId = (ins as any)?.id ?? null;
        } else if (savedId) {
          // Bump use_count on the existing entry
          const existing = addresses.find((a) => a.id === savedId);
          await supabase
            .from("direct_shipper_addresses" as any)
            .update({ use_count: (existing?.use_count ?? 0) + 1, last_used_at: new Date().toISOString() })
            .eq("id", savedId);
        }
        stopsToSave.push({ ...s, saved_address_id: savedId });
      }

      await saveDispatch(
        dispatchId,
        {
          load_pallet_count: pallets ? Number(pallets) : null,
          load_weight_lbs: weight ? Number(weight) : null,
          load_commodity: commodity.trim() || null,
          confidential_notes: notes.trim() || null,
          status: markReady
            ? (dispatchStatus === "assigned" || dispatchStatus === "in_transit" ? dispatchStatus : "ready")
            : undefined,
        } as any,
        stopsToSave,
        user.id,
        brokerProfile?.id ?? null,
        loadId,
        load?.load_number ?? null,
      );

      // Broker notification on post-ready EDITS.
      // Skips the Mark-Ready transition itself (draft→ready, handled below) and
      // stays silent when nothing actually changed. Routed through notifyClient
      // (service role) so it reaches in-app + push + email — the previous
      // client-side notifications.insert was always blocked by RLS.
      if (wasPostReady && brokerProfile?.id && load) {
        const afterSnapshot = buildDispatchSnapshot({
          pallets: pallets ? Number(pallets) : null,
          weight: weight ? Number(weight) : null,
          commodity: commodity.trim() || null,
          notes: notes.trim() || null,
          stops: stopsToSave as any[],
        });
        const changed = diffDispatchSnapshots(beforeSnapshot, afterSnapshot);
        if (changed.length > 0) {
          const summary = summarizeChanges(changed);
          const lane = `${load.origin_city}, ${load.origin_state} → ${load.destination_city}, ${load.destination_state}`;
          // Fire-and-forget — never awaited, never allowed to break the save.
          notifyClient({
            recipientUserId: brokerProfile.id,
            eventType: "dispatch_updated",
            inAppType: "dispatch_updated",
            title: wasInTransit
              ? "⚠️ Dispatch changed while in transit"
              : "Dispatch updated — review before you roll",
            body: wasInTransit
              ? `${load.load_number ?? "Load"} is already moving and the shipper changed: ${summary}. Confirm before continuing.`
              : `${load.load_number ?? "Load"} — ${lane}. Shipper changed: ${summary}.`,
            link: brokerDispatchLink(loadId),
            loadId,
            metadata: {
              direct_load_id: loadId,
              dispatch_id: dispatchId,
              load_type: "lr_direct",
              changed_fields: changed,
              in_transit: wasInTransit,
            },
            emailPayload: {
              load_number: load.load_number ?? null,
              lane,
              changed_fields: changed,
              in_transit: wasInTransit,
            },
          });
          // Timeline event so both sides have a durable record of the edit.
          supabase.from("direct_dispatch_events" as any).insert({
            dispatch_id: dispatchId,
            actor_id: user.id,
            event_type: "dispatch_edited",
            payload: { changed_fields: changed, in_transit: wasInTransit, notes: summary },
          } as any).then(() => {}, () => { /* non-fatal */ });
        }
      }


      // Broker notification on the Mark-Ready transition itself. Fires only
      // when the dispatch was NOT already past ready (so subsequent saves after
      // ready don't re-notify — those use the `dispatch_updated` path above).
      if (markReady && !wasPostReady && brokerProfile?.id && load) {
        const lane = `${load.origin_city}, ${load.origin_state} → ${load.destination_city}, ${load.destination_state}`;
        notifyClient({
          recipientUserId: brokerProfile.id,
          eventType: "dispatch_ready",
          inAppType: "dispatch_ready",
          title: "📦 Dispatch packet ready",
          body: `${load.load_number ?? "Your load"} — ${lane}`,
          link: brokerDispatchLink(loadId),
          loadId,
          metadata: { direct_load_id: loadId, dispatch_id: dispatchId, load_type: "lr_direct" },
          smsBody: `LR Direct: Dispatch ready${load.load_number ? ` for ${load.load_number}` : ""}. Open dispatch.`,
        });
      }

      toast.success(markReady ? "Dispatch marked ready" : "Dispatch saved");
      // Save succeeded — the localStorage backstop is no longer needed, the form
      // is clean again, and the next payload is allowed to re-seed it.
      if (loadId) clearDispatchDraft(loadId);
      setDirty(false);
      setRestoredDraft(false);
      seededRef.current = null;
      if (markReady && !isBrokerView) {
        // Success only: send the shipper back to their LR Direct My Loads
        // dispatching stage. No refresh needed since we leave the screen.
        navigate("/lr-direct/my-loads?stage=dispatching");
        return;
      }
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || "Failed to save dispatch");
    } finally {
      setSaving(false);
    }
  };

  // Wraps doSave with an in-transit confirmation modal when applicable.
  const requestSave = (markReady: boolean) => {
    if (isInTransit) {
      setTransitConfirm({ markReady });
      return;
    }
    doSave(markReady);
  };

  // Skeleton on FIRST load only. Background refetches (`refreshing`) never
  // unmount the form — that unmount was what destroyed in-progress edits.
  if (initialLoading || !load) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Broker view = polished confirmation, not the editable form
  if (isBrokerView && user?.id) {
    return (
      <BrokerDispatchView
        load={load}
        dispatchId={data?.dispatch?.id}
        status={dispatchStatus}
        stops={stops}
        loadStops={data?.loadStops ?? []}
        loadPallets={pallets ? Number(pallets) : null}
        loadWeight={weight ? Number(weight) : null}
        loadCommodity={commodity || null}
        loadNotes={notes || null}
        awardedAmount={awardedAmount}
        totalMiles={load.total_miles ? Number(load.total_miles) : null}
        awardedAt={awardedAt}
        loadId={loadId!}
        actorId={user.id}
        shipperUserId={load.created_by ?? null}
        dispatchPreparedAt={(data?.dispatch as any)?.dispatch_prepared_at ?? null}
        onRefresh={refresh}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Back to all loads"
            onClick={() => navigate("/lr-direct/my-loads?stage=all", { replace: true })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-9 w-9 rounded-md bg-brand-teal/10 flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-brand-teal" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground truncate">
              Dispatch — {load.load_number}
            </h1>
            <p className="text-sm text-muted-foreground truncate">
              {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
              {load.total_miles ? ` · ${Math.round(Number(load.total_miles)).toLocaleString()} mi` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-start sm:justify-end w-full sm:w-auto">
          <DispatchStatusBadge status={dispatchStatus} />
          {isOwner && !lockedView && (
            <>
              <TemplatePickerDropdown
                templates={templates}
                loading={tplLoading}
                hasUnsavedContent={hasUnsavedContent}
                onApply={applyTemplate}
                onDelete={(id) => removeTemplate.mutateAsync(id)}
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setSaveTplOpen(true)}
                title="Save the current stops, pallets, weight, commodity and notes as a reusable template"
              >
                <BookmarkIcon className="h-4 w-4" /> Save as Template
              </Button>
            </>
          )}
          {/* RETIRED: the broker/carrier status dropdown lived here. Milestone
              progression is now canonical-only — brokers advance the load from
              the dispatch view's tracker + NEXT UP action. */}
          {isOwner && dispatchStatus === "delivered" && data?.dispatch?.id && (
            <Button
              size="sm" variant="outline" className="gap-1.5 border-emerald-500/40 text-emerald-700"
              onClick={async () => {
                try {
                  await closeLoadManual({
                    dispatchId: data.dispatch!.id,
                    loadId: loadId!,
                    actorId: user!.id,
                    counterpartyUserId: brokerProfile?.id ?? null,
                    reason: "Closed by shipper from the dispatch builder",
                  });
                  toast.success("Load closed");
                  refresh();
                } catch (e: any) {
                  toast.error(e?.message || "Failed to close");
                }
              }}
            >
              <CheckCircle2 className="h-4 w-4" /> Close Dispatch
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => window.open(`/lr-direct/dispatch/${loadId}`, "_blank")} className="gap-1.5">
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`/lr-direct/dispatch/${loadId}`, "_blank")}
            className="gap-1.5"
            title="Choose 'Save as PDF' in the print dialog"
          >
            <Download className="h-4 w-4" /> Download PDF
          </Button>
          {isBrokerView && (
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={() => openChatWithUser(load.created_by, "Shipper", undefined, loadId)}>
              <MessageSquare className="h-4 w-4" /> Message Shipper
            </Button>
          )}
          {isOwner && brokerProfile && (
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={() => openChatWithUser(brokerProfile.id, brokerProfile.name, undefined, loadId)}>
              <MessageSquare className="h-4 w-4" /> Message Broker
            </Button>
          )}
        </div>
      </div>

      {/* Restored-draft bar (localStorage backstop) */}
      {restoredDraft && !lockedView && (
        <Card className="p-3 mb-4 border-amber-500/40 bg-amber-500/10">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-foreground">
              <span className="font-medium">Restored unsaved changes.</span>{" "}
              <span className="text-muted-foreground">
                We recovered edits you hadn't saved yet. Save to keep them, or discard to go back to the saved version.
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={discardDraft}>Discard</Button>
          </div>
        </Card>
      )}



      {/* Award banner */}
      {isBrokerView && (
        <Card className="p-3 mb-4 border-brand-teal/30 bg-brand-teal/5">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-brand-teal" />
            <span className="font-medium">You're awarded this load.</span>
            <span className="text-muted-foreground">Full dispatch details below — treat as your rate confirmation.</span>
          </div>
        </Card>
      )}

      {/* Stops */}
      <div className="space-y-3 mb-4">
        {stops.map((stop, idx) => (
          <Card key={idx} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center h-5 w-5 rounded text-[9px] font-bold uppercase ${stop.stop_type === "pickup" ? "bg-brand-teal/15 text-brand-teal" : "bg-destructive/10 text-destructive"}`}>
                  {stop.stop_type === "pickup" ? "P" : "D"}
                </span>
                <h3 className="text-sm font-semibold uppercase tracking-wider">
                  Stop {idx + 1} — {stop.stop_type === "pickup" ? "Pickup" : "Delivery"}
                </h3>
              </div>
              <div className="flex items-center gap-1">
                {/* Reorder — hidden once the truck is rolling: tracker stage keys
                    derive from stop_type + stop_number, so renumbering a live
                    dispatch would orphan recorded completion events. */}
                {!lockedView && !isInTransit && stops.length > 1 && (
                  <>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" disabled={idx === 0}
                      aria-label="Move stop earlier in the route" onClick={() => moveStop(idx, -1)}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" disabled={idx === stops.length - 1}
                      aria-label="Move stop later in the route" onClick={() => moveStop(idx, 1)}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                {!lockedView && stops.length > 1 && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeStop(idx)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

            </div>

            {!lockedView && (
              <div className="mb-3">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Facility / Company {addresses.length > 0 && <span className="text-muted-foreground/70 normal-case font-normal ml-1">— start typing to search your address book</span>}
                </label>
                <div className="mt-1">
                  <AddressBookAutocomplete
                    value={stop.name ?? ""}
                    addresses={addresses}
                    disabled={lockedView}
                    placeholder="e.g. Acme Distribution — type to filter…"
                    onChange={(v) => updateStop(idx, { name: v, saved_address_id: null })}
                    onSelect={(a) => applyAddress(idx, a)}
                  />
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {lockedView && (
                <Field label="Facility / Company">
                  <Input value={stop.name ?? ""} disabled readOnly />
                </Field>
              )}
              <Field label="Street address">
                <Input value={stop.address ?? ""} disabled={lockedView} onChange={(e) => updateStop(idx, { address: e.target.value, saved_address_id: null })} />
              </Field>
              <Field label="City">
                <Input value={stop.city ?? ""} disabled={lockedView} onChange={(e) => updateStop(idx, { city: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="State">
                  <Input value={stop.state ?? ""} disabled={lockedView} maxLength={2}
                    onChange={(e) => updateStop(idx, { state: e.target.value.toUpperCase() })} />
                </Field>
                <Field label="ZIP">
                  <Input value={stop.postal_code ?? ""} disabled={lockedView} onChange={(e) => updateStop(idx, { postal_code: e.target.value })} />
                </Field>
              </div>
              <Field label={stop.stop_type === "pickup" ? "Pickup date" : "Delivery date"}>
                <DatePickerField
                  value={stop.scheduled_date ?? null}
                  disabled={lockedView}
                  onChange={(v) => updateStop(idx, { scheduled_date: v })}
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Window start">
                  <Input value={stop.scheduled_time_start ?? ""} disabled={lockedView} placeholder="08:00"
                    onChange={(e) => updateStop(idx, { scheduled_time_start: e.target.value })} />
                </Field>
                <Field label="Window end">
                  <Input value={stop.scheduled_time_end ?? ""} disabled={lockedView} placeholder="17:00"
                    onChange={(e) => updateStop(idx, { scheduled_time_end: e.target.value })} />
                </Field>
              </div>
              <Field label="Contact name">
                <Input value={stop.contact_name ?? ""} disabled={lockedView} onChange={(e) => updateStop(idx, { contact_name: e.target.value })} />
              </Field>
              <Field label="Contact phone">
                <Input value={stop.contact_phone ?? ""} disabled={lockedView} onChange={(e) => updateStop(idx, { contact_phone: e.target.value })} />
              </Field>
              <Field label={stop.stop_type === "pickup" ? "Pickup # / PO" : "PRO / Delivery #"}>
                <Input value={stop.reference_number ?? ""} disabled={lockedView}
                  placeholder={stop.stop_type === "pickup" ? "PO12345" : "PRO789"}
                  onChange={(e) => updateStop(idx, { reference_number: e.target.value })} />
              </Field>
              <Field label="Pallets">
                <Input type="number" min={0} value={stop.pallet_count ?? ""} disabled={lockedView}
                  onChange={(e) => updateStop(idx, { pallet_count: e.target.value === "" ? null : Number(e.target.value) })} />
              </Field>
            </div>

            <div className="mt-3">
              <Field label={<><Lock className="inline h-3 w-3 mr-1 text-brand-teal" />Notes (awarded broker only)</>}>
                <Textarea value={stop.confidential_notes ?? ""} disabled={lockedView} className="min-h-[60px]"
                  placeholder="Gate code, ask for Maria, special instructions, etc."
                  onChange={(e) => updateStop(idx, { confidential_notes: e.target.value })} />
              </Field>
            </div>

            {!lockedView && !stop.saved_address_id && (stop.name || stop.address) && (
              <label className="mt-3 flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                <Checkbox
                  checked={stop._saveToBook !== false}
                  onCheckedChange={(c) => updateStop(idx, { _saveToBook: !!c })}
                />
                <BookmarkPlus className="h-3.5 w-3.5 text-brand-teal" />
                Save this address to my directory for next time
              </label>
            )}
            {!lockedView && stop.saved_address_id && (
              <p className="mt-3 text-[11px] text-muted-foreground flex items-center gap-1.5">
                <BookUser className="h-3 w-3 text-brand-teal" /> Linked to your address book
              </p>
            )}
          </Card>
        ))}

        {!lockedView && (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" onClick={() => addStop("pickup")} className="gap-1"><Plus className="h-4 w-4" /> Add Pickup</Button>
            <Button variant="outline" size="sm" onClick={() => addStop("delivery")} className="gap-1"><Plus className="h-4 w-4" /> Add Delivery</Button>
          </div>
        )}
      </div>

      {/* Load-level packet */}
      <Card className="p-4 mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3">Load packet</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Total pallets">
            <Input type="number" min={0} value={pallets} disabled={lockedView} onChange={(e) => { markDirty(); setPallets(e.target.value); }} />
          </Field>
          <Field label="Total weight (lbs)">
            <Input type="number" min={0} value={weight} disabled={lockedView} onChange={(e) => { markDirty(); setWeight(e.target.value); }} />
          </Field>
          <Field label="Commodity">
            <Input value={commodity} disabled={lockedView} onChange={(e) => { markDirty(); setCommodity(e.target.value); }} />
          </Field>
        </div>
        <div className="mt-3">
          <Field label={<><Lock className="inline h-3 w-3 mr-1 text-brand-teal" />Load-level notes (awarded broker only)</>}>
            <Textarea value={notes} disabled={lockedView} className="min-h-[70px]"
              placeholder="Anything the awarded broker should know but bidders shouldn't see."
              onChange={(e) => { markDirty(); setNotes(e.target.value); }} />
          </Field>
        </div>
      </Card>

      {/* Phase 2 — Documents + Timeline (only when dispatch row exists) */}
      {data?.dispatch?.id && (
        <div className="grid gap-4 md:grid-cols-2 mb-4">
          <DispatchDocumentsPanel
            dispatchId={data.dispatch.id}
            loadId={loadId!}
            uploaderId={user!.id}
            counterpartyUserId={isOwner ? (brokerProfile?.id ?? null) : load.created_by}
            shipperUserId={load.created_by ?? null}
            canUpload={isOwner || isBrokerView}
          />
          <DispatchTimeline dispatchId={data.dispatch.id} />
        </div>
      )}

      {/* Read-only banner for delivered/closed loads */}
      {lockedView && !readOnly && (
        <Card className="p-3 mb-4 border-muted bg-muted/30">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Eye className="h-4 w-4" />
            <span className="font-medium text-foreground">View only</span>
            <span>· this dispatch is {dispatchStatus === "closed" ? "closed" : "complete"} and can no longer be edited.</span>
          </div>
        </Card>
      )}

      {/* Pre-award banner (Bidding stage — no broker attached yet) */}
      {isPreAward && isOwner && (
        <Card className="p-3 mb-4 border-amber-400/40 bg-amber-50 dark:bg-amber-950/20">
          <div className="flex items-center gap-2 text-sm">
            <ClipboardList className="h-4 w-4 text-amber-600" />
            <span className="font-medium text-foreground">Preparing dispatch</span>
            <span className="text-muted-foreground">· no broker awarded yet. Your prep will carry over automatically when you award a bid.</span>
          </div>
        </Card>
      )}

      {/* Footer actions */}
      {!lockedView && (
        <div className="flex w-full max-w-full min-w-0 items-center justify-end gap-2 flex-wrap sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border py-3 -mx-3 px-3 sm:static sm:bg-transparent sm:backdrop-blur-none sm:border-0 sm:p-0 sm:mx-0">
          <Button variant="outline" onClick={() => requestSave(false)} disabled={saving} className="gap-1.5 flex-1 sm:flex-none h-11 sm:h-9">
            <Save className="h-4 w-4" /> Save draft
          </Button>
          {!isPreAward && (
            <Button onClick={() => requestSave(true)} disabled={saving} className="gap-1.5 bg-brand-teal hover:bg-brand-teal/90 flex-1 sm:flex-none h-11 sm:h-9">
              <ShieldCheck className="h-4 w-4" /> Mark ready
            </Button>
          )}
        </div>
      )}

      <SaveAsTemplateModal
        open={saveTplOpen}
        onOpenChange={setSaveTplOpen}
        existingNames={templates.map((t) => t.name)}
        onSave={async ({ name, description }) => {
          await saveTemplate.mutateAsync({ name, description, template_data: buildTemplatePayload() });
        }}
      />

      <AlertDialog open={!!transitConfirm} onOpenChange={(open) => !open && setTransitConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit dispatch mid-transit?</AlertDialogTitle>
            <AlertDialogDescription>
              This load is currently in transit. Editing dispatch info may affect the broker's pickup or delivery. The broker will be notified of these changes. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const pending = transitConfirm;
                setTransitConfirm(null);
                if (pending) doSave(pending.markReady);
              }}
            >
              Confirm Edit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
